import api from './api';

/**
 * Plan-related API utilities with impact checking
 */
export const planApi = {
  /**
   * Get plan impact information (active users, pending registrations, etc.)
   * @param {string} planId - Plan ID
   * @returns {Promise<Object>} Plan impact data
   */
  async getPlanImpact(planId) {
    const response = await api.get(`/plans/${planId}/impact`);
    return response.data.data;
  },

  /**
   * Update a plan with automatic warning handling
   * @param {string} planId - Plan ID
   * @param {Object} planData - Plan update data
   * @returns {Promise<Object>} Update response with warning information
   */
  async updatePlan(planId, planData) {
    const response = await api.put(`/plans/${planId}`, planData);
    return {
      plan: response.data.data,
      warning: response.data.warning || null,
      activeUsersCount: response.data.activeUsersCount || 0,
      success: response.data.success
    };
  },

  /**
   * Create a new plan
   * @param {Object} planData - Plan data
   * @returns {Promise<Object>} Created plan data
   */
  async createPlan(planData) {
    const response = await api.post('/plans', planData);
    return response.data.data;
  },

  /**
   * Delete a plan with impact checking
   * @param {string} planId - Plan ID
   * @returns {Promise<Object>} Delete response
   */
  async deletePlan(planId) {
    // Get impact first to warn user
    const impact = await this.getPlanImpact(planId);
    const response = await api.delete(`/plans/${planId}`);
    return {
      success: response.data.success,
      impact
    };
  },

  /**
   * Get all plans
   * @returns {Promise<Array>} List of plans
   */
  async getAllPlans() {
    const response = await api.get('/plans/all');
    return response.data.data;
  },

  /**
   * Check if plan can be safely edited (no active users)
   * @param {string} planId - Plan ID
   * @returns {Promise<boolean>} Whether plan can be safely edited
   */
  async canSafelyEditPlan(planId) {
    try {
      const impact = await this.getPlanImpact(planId);
      return impact.impact.canSafelyEdit;
    } catch (error) {
      console.warn('Could not check plan impact:', error);
      return true; // Default to allowing edits if check fails
    }
  },

  /**
   * Get plan impact summary for display
   * @param {string} planId - Plan ID
   * @returns {Promise<Object>} Impact summary
   */
  async getPlanImpactSummary(planId) {
    try {
      const impact = await this.getPlanImpact(planId);
      return {
        hasImpact: impact.impact.totalActiveUsers > 0 || impact.impact.pendingRegistrations > 0,
        totalActiveUsers: impact.impact.totalActiveUsers,
        pendingRegistrations: impact.impact.pendingRegistrations,
        expiredUsersLast30Days: impact.impact.expiredUsersLast30Days,
        canSafelyEdit: impact.impact.canSafelyEdit,
        warningMessage: impact.impact.warningMessage,
        activeUsersPreview: impact.activeUsersPreview
      };
    } catch (error) {
      console.warn('Could not get plan impact summary:', error);
      return {
        hasImpact: false,
        totalActiveUsers: 0,
        pendingRegistrations: 0,
        expiredUsersLast30Days: 0,
        canSafelyEdit: true,
        warningMessage: null,
        activeUsersPreview: []
      };
    }
  }
};

export default planApi;